export type SettingsPath =
  | "/settings/account"
  | "/settings/your-context"
  | "/settings/security"
  | "/settings/general"
  | "/settings/appearance"
  | "/settings/keybindings"
  | "/settings/archived"
  | "/settings/organization"
  | "/settings/members"
  | "/settings/usage"
  | "/settings/branding";

export interface SettingsSearchItem {
  readonly id: string;
  readonly title: string;
  readonly to: SettingsPath;
  readonly targetId?: string;
  /**
   * Option labels and words people may remember instead of the title.
   * Adapted from pingdotgg/t3code (MIT), settingsSearch.ts at 7202ba666.
   */
  readonly searchTerms?: ReadonlyArray<string>;
}

/**
 * Section labels in sidebar order. The sidebar nav and the search-result
 * subtitles both render from this record, so each label exists once.
 */
export const SETTINGS_SECTION_LABELS: Readonly<Record<SettingsPath, string>> = {
  "/settings/account": "Profile",
  "/settings/your-context": "Your context",
  "/settings/general": "Preferences",
  "/settings/appearance": "Appearance",
  "/settings/keybindings": "Keybindings",
  "/settings/security": "Security",
  "/settings/archived": "Archive",
  "/settings/organization": "General",
  "/settings/members": "Members",
  "/settings/usage": "Usage & allowances",
  "/settings/branding": "Branding",
};

/**
 * Sidebar nav groups, in render order: everything personal under Account
 * (who you are, how the workspace behaves for you), then everything that
 * applies to the whole organization under Organization.
 */
export const SETTINGS_NAV_GROUPS: ReadonlyArray<{
  readonly label: string;
  readonly paths: ReadonlyArray<SettingsPath>;
}> = [
  {
    label: "Account",
    paths: [
      "/settings/account",
      "/settings/your-context",
      "/settings/general",
      "/settings/appearance",
      "/settings/keybindings",
      "/settings/security",
      "/settings/archived",
    ],
  },
  {
    label: "Organization",
    paths: [
      "/settings/organization",
      "/settings/members",
      "/settings/usage",
      "/settings/branding",
    ],
  },
];

/**
 * Old section slugs that moved, mapped to where they live now, so bookmarks
 * and links from before the move still land.
 */
export const SETTINGS_SECTION_ALIASES: Readonly<Record<string, SettingsPath>> = {
  team: "/settings/members",
};

/**
 * Every searchable setting, in result order. This catalog is the single
 * source of truth for anchor ids and visible titles: panels render both via
 * `searchableSetting`, so a retitle (or, later, a translation pass) happens
 * here once instead of separately in the panel and the index.
 */
export const SETTINGS_SEARCH_ITEMS = [
  {
    id: "your-context-recall",
    title: "Use past conversations automatically",
    to: "/settings/your-context",
  },
  {
    id: "your-context-learning",
    title: "Learn from my conversations",
    to: "/settings/your-context",
  },
  { id: "your-context-role", title: "Role", to: "/settings/your-context" },
  { id: "your-context-about", title: "About you", to: "/settings/your-context" },
  { id: "your-context-style", title: "How you work", to: "/settings/your-context" },
  { id: "your-context-answers", title: "How you want answers", to: "/settings/your-context" },
  { id: "your-context-instructions", title: "Standing instructions", to: "/settings/your-context" },
  {
    id: "profile-picture",
    title: "Profile picture",
    to: "/settings/account",
  },
  {
    id: "account-name",
    title: "Name",
    to: "/settings/account",
  },
  {
    id: "account-email",
    title: "Email",
    to: "/settings/account",
  },
  {
    id: "active-sessions",
    title: "Where you're signed in",
    to: "/settings/security",
  },
  {
    id: "past-sessions",
    title: "Past sign-ins",
    to: "/settings/security",
  },
  {
    id: "browser-logins",
    title: "Remember my browser logins",
    to: "/settings/security",
  },
  {
    id: "browser-logins-organization",
    title: "Let members save browser logins",
    to: "/settings/organization",
  },
  {
    id: "analytics-consent",
    title: "Product analytics",
    to: "/settings/security",
  },
  {
    id: "color-scheme",
    title: "Color scheme",
    to: "/settings/appearance",
    // The scheme tiles sit at the top of the Appearance section.
    targetId: "appearance",
  },
  {
    id: "theme",
    title: "Themes",
    to: "/settings/appearance",
    // Theme cards live directly under the scheme tiles; the section is the
    // stable scroll destination for both.
    targetId: "appearance",
  },
  {
    // Prefixed because the slider control already owns the `glass-opacity` id.
    id: "setting-glass-opacity",
    title: "Glass opacity",
    to: "/settings/appearance",
  },
  {
    id: "environment-identification",
    title: "Environment identification",
    to: "/settings/appearance",
    // The setting is stage-dependent, so its parent section is the stable destination.
    targetId: "appearance",
  },
  {
    id: "interface-font",
    title: "Interface font",
    to: "/settings/appearance",
  },
  {
    id: "prompt-font",
    title: "Prompt font",
    to: "/settings/appearance",
  },
  {
    id: "code-font",
    title: "Code font",
    to: "/settings/appearance",
  },
  {
    id: "terminal-font",
    title: "Terminal font",
    to: "/settings/appearance",
  },
  {
    id: "font-smoothing",
    title: "Font smoothing",
    to: "/settings/appearance",
  },
  {
    id: "word-wrap",
    title: "Word wrap",
    to: "/settings/appearance",
  },
  {
    id: "rail-labels",
    title: "Bigger rail icons",
    to: "/settings/general",
  },
  {
    id: "project-grouping",
    title: "Project grouping",
    to: "/settings/general",
  },
  {
    id: "auto-settle-inactive-threads",
    title: "Auto-settle inactive threads",
    to: "/settings/general",
  },
  {
    id: "time-format",
    title: "Time format",
    to: "/settings/general",
  },
  {
    id: "hide-whitespace-changes",
    title: "Hide whitespace changes",
    to: "/settings/general",
  },
  {
    id: "composer-collapse",
    title: "Collapse composer",
    to: "/settings/general",
  },
  {
    id: "follow-up-behavior",
    title: "Follow-up behavior",
    to: "/settings/general",
    searchTerms: ["steer", "interrupt", "queue", "send while running", "reply in progress"],
  },
  {
    id: "provider-update-checks",
    title: "Provider update checks",
    to: "/settings/general",
  },
  {
    id: "new-threads",
    title: "New threads",
    to: "/settings/general",
  },
  {
    id: "start-from-origin",
    title: "Start from origin",
    to: "/settings/general",
    targetId: "new-threads",
  },
  {
    id: "add-project-starts-in",
    title: "Add project starts in",
    to: "/settings/general",
  },
  {
    id: "archive-confirmation",
    title: "Archive confirmation",
    to: "/settings/general",
  },
  {
    id: "delete-confirmation",
    title: "Delete confirmation",
    to: "/settings/general",
  },
  {
    id: "text-generation-model",
    title: "Text generation model",
    to: "/settings/general",
  },
  {
    id: "diagnostics",
    title: "Diagnostics",
    to: "/settings/general",
  },
  { id: "thread-notifications", title: "Thread notifications", to: "/settings/general" },
  { id: "in-app-notifications", title: "In-app thread notifications", to: "/settings/general" },
  {
    id: "response-streaming",
    title: "Response streaming",
    to: "/settings/general",
  },
  {
    id: "keybindings",
    title: "Keybindings",
    to: "/settings/keybindings",
  },
  {
    id: "archive",
    title: "Archived threads",
    to: "/settings/archived",
  },
  {
    id: "organization-name",
    title: "Organization name",
    to: "/settings/organization",
  },
  {
    id: "company-profile",
    title: "Company context",
    to: "/settings/organization",
  },
  {
    id: "model-usage",
    title: "Member allowances",
    to: "/settings/usage",
    searchTerms: ["on-demand model usage", "budget", "spend", "limit", "cap", "billing"],
  },
  {
    id: "team-members",
    title: "Members",
    to: "/settings/members",
    searchTerms: ["team", "people", "admins", "roles", "remove member"],
  },
  {
    id: "team-invitations",
    title: "Invitations",
    to: "/settings/members",
    searchTerms: ["invite", "add member"],
  },
  {
    id: "organization-logo",
    title: "Organization logo",
    to: "/settings/branding",
  },
  {
    id: "brand-colors",
    title: "Brand colours",
    to: "/settings/branding",
  },
  {
    id: "tone-of-voice",
    title: "Tone of voice",
    to: "/settings/branding",
  },
  {
    id: "brand-guidelines",
    title: "Brand guidelines",
    to: "/settings/branding",
  },
] as const satisfies ReadonlyArray<SettingsSearchItem>;

export type SettingsSearchItemId = (typeof SETTINGS_SEARCH_ITEMS)[number]["id"];

const SEARCH_ITEMS_BY_ID = Object.fromEntries(
  SETTINGS_SEARCH_ITEMS.map((item) => [item.id, item]),
) as Readonly<Record<SettingsSearchItemId, SettingsSearchItem>>;

/**
 * `id` and `title` props for the element a search item anchors to. Panels
 * spread (or pick from) this instead of restating the strings, so the catalog
 * and the rendered settings cannot drift apart.
 */
export function searchableSetting(id: SettingsSearchItemId): {
  readonly id: string;
  readonly title: string;
} {
  const { id: anchorId, title } = SEARCH_ITEMS_BY_ID[id];
  return { id: anchorId, title };
}

function normalizeSearchText(value: string): string {
  return value
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLocaleLowerCase()
    .replace(/\s+/g, " ")
    .trim();
}

export function searchSettings(
  query: string,
  items: ReadonlyArray<SettingsSearchItem> = SETTINGS_SEARCH_ITEMS,
): ReadonlyArray<SettingsSearchItem> {
  const normalizedQuery = normalizeSearchText(query);
  if (normalizedQuery.length === 0) return [];

  return items.filter(
    (item) =>
      normalizeSearchText(item.title).includes(normalizedQuery) ||
      (item.searchTerms ?? []).some((term) => normalizeSearchText(term).includes(normalizedQuery)),
  );
}
